import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Package, Truck } from 'lucide-react';
import { toast } from 'sonner';

interface MaterialRow {
  id: string;
  empresa_id: string;
  orden_trabajo_id: string;
  holded_pedido_compra_id: string;
  holded_pedido_compra_numero: string | null;
  descripcion: string;
  sku: string | null;
  proveedor: string | null;
  unidades_pedidas: number;
  unidades_recibidas: number;
  fecha_recepcion: string | null;
  recibido_por: string | null;
  recepcion_id: string | null;
}

interface Props {
  ordenTrabajoId: string;
  empresaId: string;
  clienteId?: string | null;
}

export function OrdenTrabajoMateriales({ ordenTrabajoId, empresaId, clienteId }: Props) {
  const [rows, setRows] = useState<MaterialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from('orden_trabajo_materiales')
      .select('*')
      .eq('orden_trabajo_id', ordenTrabajoId)
      .order('holded_pedido_compra_numero', { ascending: true })
      .order('descripcion', { ascending: true });
    if (error) {
      toast.error(`Error cargando materiales: ${error.message}`);
      return;
    }
    setRows((data as MaterialRow[]) || []);
  };

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [ordenTrabajoId]);

  const pedidos = useMemo(() => {
    const map = new Map<string, MaterialRow[]>();
    for (const r of rows) {
      const arr = map.get(r.holded_pedido_compra_id) || [];
      arr.push(r);
      map.set(r.holded_pedido_compra_id, arr);
    }
    return Array.from(map.entries()).map(([poId, lineas]) => ({
      poId,
      numero: lineas[0].holded_pedido_compra_numero,
      proveedor: lineas[0].proveedor,
      lineas,
      recibidas: lineas.filter((l) => l.unidades_recibidas >= l.unidades_pedidas).length,
    }));
  }, [rows]);

  const total = rows.length;
  const recibidas = rows.filter((r) => r.unidades_recibidas >= r.unidades_pedidas).length;

  // Crea o actualiza una recepción resumen por pedido de compra en el módulo Recepciones,
  // y devuelve el id para vincular a las líneas.
  const ensureRecepcionForPedido = async (poId: string, poNumero: string | null, proveedor: string | null): Promise<string | null> => {
    const { data: existente } = await supabase
      .from('recepciones_material')
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('holded_pedido_compra_id', poId)
      .maybeSingle();
    if (existente) return existente.id;

    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return null;

    const { data: nueva, error } = await supabase
      .from('recepciones_material')
      .insert({
        empresa_id: empresaId,
        usuario_id: userData.user.id,
        fecha_recepcion: new Date().toISOString(),
        orden_trabajo_id: ordenTrabajoId,
        ubicacion: 'obra',
        proveedor,
        origen: 'pedido_compra_holded',
        holded_pedido_compra_id: poId,
        holded_pedido_compra_numero: poNumero,
        notas: `Auto-generada desde pedido de compra Holded${poNumero ? ` #${poNumero}` : ''}`,
      })
      .select('id')
      .single();
    if (error) {
      toast.error(`No se pudo crear la recepción: ${error.message}`);
      return null;
    }
    return nueva.id;
  };

  const toggleLinea = async (row: MaterialRow, recibido: boolean) => {
    setSaving(row.id);
    try {
      let recepcionId = row.recepcion_id;
      if (recibido && !recepcionId) {
        recepcionId = await ensureRecepcionForPedido(
          row.holded_pedido_compra_id,
          row.holded_pedido_compra_numero,
          row.proveedor,
        );
      }
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('orden_trabajo_materiales')
        .update({
          unidades_recibidas: recibido ? row.unidades_pedidas : 0,
          fecha_recepcion: recibido ? new Date().toISOString() : null,
          recibido_por: recibido ? userData.user?.id ?? null : null,
          recepcion_id: recibido ? recepcionId : null,
        })
        .eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Error al actualizar la línea');
    } finally {
      setSaving(null);
    }
  };

  const recibirTodoPedido = async (poId: string) => {
    const lineas = rows.filter((r) => r.holded_pedido_compra_id === poId);
    const pending = lineas.filter((r) => r.unidades_recibidas < r.unidades_pedidas);
    if (pending.length === 0) return;
    setSaving(poId);
    try {
      const primero = lineas[0];
      const recepcionId =
        primero.recepcion_id ||
        (await ensureRecepcionForPedido(poId, primero.holded_pedido_compra_numero, primero.proveedor));
      const { data: userData } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const updates = pending.map((r) =>
        supabase
          .from('orden_trabajo_materiales')
          .update({
            unidades_recibidas: r.unidades_pedidas,
            fecha_recepcion: now,
            recibido_por: userData.user?.id ?? null,
            recepcion_id: recepcionId,
          })
          .eq('id', r.id),
      );
      await Promise.all(updates);
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Error al recibir el pedido');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando materiales…
      </div>
    );
  }

  if (total === 0) {
    return (
      <Card className="bg-muted/20">
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-60" />
          <p>No hay pedidos de compra vinculados a esta OT todavía.</p>
          <p className="text-xs mt-1">
            Cuando el gestor cree un pedido de compra en Holded referenciando este pedido de venta, las líneas aparecerán aquí automáticamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  const faltan = total - recibidas;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Package className="h-4 w-4" />
          <span className="font-medium">Material recibido</span>
          <Badge variant={faltan === 0 ? 'default' : 'secondary'}>
            {recibidas}/{total}
          </Badge>
        </div>
        {faltan > 0 && (
          <Badge variant="outline" className="border-amber-500/50 text-amber-500">
            Faltan {faltan} línea{faltan === 1 ? '' : 's'}
          </Badge>
        )}
      </div>

      {pedidos.map((p) => {
        const completo = p.recibidas === p.lineas.length;
        return (
          <Card key={p.poId}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Truck className="h-4 w-4" />
                    Pedido de compra {p.numero ? `#${p.numero}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.proveedor || 'Proveedor sin especificar'} · {p.recibidas}/{p.lineas.length} recibidas
                  </div>
                </div>
                {!completo && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={saving === p.poId}
                    onClick={() => recibirTodoPedido(p.poId)}
                  >
                    {saving === p.poId ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Recibir todo'}
                  </Button>
                )}
              </div>

              <div className="divide-y">
                {p.lineas.map((l) => {
                  const recibido = l.unidades_recibidas >= l.unidades_pedidas;
                  return (
                    <div key={l.id} className="flex items-start gap-3 py-2">
                      <Checkbox
                        checked={recibido}
                        disabled={saving === l.id}
                        onCheckedChange={(v) => toggleLinea(l, !!v)}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm ${recibido ? 'line-through text-muted-foreground' : ''}`}>
                          {l.unidades_pedidas !== 1 ? `${l.unidades_pedidas} × ` : ''}
                          {l.descripcion}
                        </p>
                        {l.sku && <p className="text-xs text-muted-foreground">SKU: {l.sku}</p>}
                        {recibido && l.fecha_recepcion && (
                          <p className="text-xs text-emerald-500">
                            Recibido {new Date(l.fecha_recepcion).toLocaleDateString('es-ES')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
